import classnames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  RefAttributes,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Image, ImageProps } from '../image';

import styles from './animated-image.scss';
import { DEFAULT_PLAYER_PROPS, LOADING_STATUSES, PLACEHOLDER_STATUSES } from './config';

type PlayerProps = {
  className?: string;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  onError?: () => void;
  onLoadStart?: () => void;
  onLoadedData?: () => void;
  [K: string]: unknown;
};

export type Props = {
  src: string;
  width?: number;
  height?: number;
  className?: string;
  dataTestId?: string;
  playerProps?: PlayerProps;
  placeholderProps?: ImageProps;
} & RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement>;

const COMPONENT_ID = 'modulor-animated-image';

const DEFAULT_EMPTY_OBJECT = {};

export const AnimatedImage: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      src,
      width,
      height,
      playerProps = DEFAULT_EMPTY_OBJECT as PlayerProps,
      placeholderProps = DEFAULT_EMPTY_OBJECT as ImageProps,
      style = DEFAULT_EMPTY_OBJECT,
      dataTestId = COMPONENT_ID,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const container = useRef<HTMLDivElement>(null);
    const [loadingStatus, setLoadingStatus] = useState(LOADING_STATUSES.Initial);

    useImperativeHandle(ref, () => container.current);

    const onError = useCallback(() => {
      setLoadingStatus(LOADING_STATUSES.Error);

      playerProps.onError && playerProps.onError();
    }, [playerProps.onError]);

    const onLoadStart = useCallback(() => {
      setLoadingStatus(LOADING_STATUSES.Loading);

      playerProps.onLoadStart && playerProps.onLoadStart();
    }, [playerProps.onLoadStart]);

    const onLoadedData = useCallback(() => {
      setLoadingStatus(LOADING_STATUSES.Loaded);

      playerProps.onLoadedData && playerProps.onLoadedData();
    }, [playerProps.onLoadedData]);

    const fitStyles = useMemo(
      () => ({
        width: width || '100%',
        height: height || '100%',
      }),
      [width, height],
    );

    const shouldRenderPlaceholder = useMemo(
      () => PLACEHOLDER_STATUSES.includes(loadingStatus),
      [loadingStatus],
    );

    const placeholder = useMemo(
      () =>
        children || (
          <Image
            width={width && `${width}`}
            height={height && `${height}`}
            style={{
              ...fitStyles,
            }}
            {...placeholderProps}
            className={classnames(styles.placeholder, placeholderProps.className, {
              [styles['adjust-width']]: !width,
              [styles['adjust-height']]: !height,
            })}
          />
        ),
      [children, placeholderProps, fitStyles, width, height],
    );

    return (
      <div
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}
        data-id={dataTestId}
        ref={container}
        className={classnames(styles.wrapper, className)}
        style={{
          ...fitStyles,
          ...style,
        }}
        {...rest}>
        <video
          key={src}
          width={width}
          height={height}
          {...DEFAULT_PLAYER_PROPS}
          {...playerProps}
          className={classnames(styles.player, playerProps.className, {
            [styles['adjust-width']]: !width,
            [styles['adjust-height']]: !height,
            [styles.hidden]: shouldRenderPlaceholder,
          })}
          onError={onError}
          onLoadStart={onLoadStart}
          onLoadedData={onLoadedData}>
          <source src={src} type="video/mp4" />

          {placeholder}
        </video>
        {shouldRenderPlaceholder && placeholder}
      </div>
    );
  },
);

AnimatedImage.displayName = 'AnimatedImage';
